/**
 * PureOne Hero Scroll Animation
 * Apple iPhone 17 Pro inspired - product fills viewport, scales down on scroll
 *
 * @package DailyHydrogen
 * @since 2.4.0
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

class PureOneHeroScroll {
  constructor() {
    this.hero = document.querySelector('.pureone-hero');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.prefersReducedMotion) {
      this.showStaticLayout();
      return;
    }

    if (!this.hero) {
      return;
    }

    this.init();
  }

  showStaticLayout() {
    const trustSignals = document.querySelector('#trust-signals');
    const productImage = document.querySelector('.pureone-hero__image');
    const imageWrapper = document.querySelector('.pureone-hero__image-wrapper');

    if (trustSignals) {
      trustSignals.style.opacity = '1';
      trustSignals.style.transform = 'none';
    }
    if (productImage) {
      productImage.style.opacity = '1';
      productImage.style.transform = 'none';
    }
    if (imageWrapper) {
      imageWrapper.style.overflow = 'visible';
    }
  }

  init() {
    this.imageWrapper = this.hero.querySelector('.pureone-hero__image-wrapper');
    this.productImage = this.hero.querySelector('.pureone-hero__image');
    this.trustSignals = this.hero.querySelector('#trust-signals');

    if (!this.productImage) {
      return;
    }

    const img = this.productImage.querySelector('img');
    if (img) {
      if (img.complete) {
        this.setupAnimation();
      } else {
        img.addEventListener('load', () => this.setupAnimation());
      }
    } else {
      this.setupAnimation();
    }
  }

  setupAnimation() {
    const hero = this.hero;
    const imageWrapper = this.imageWrapper;
    const productImage = this.productImage;
    const trustSignals = this.trustSignals;

    // ========================================
    // APPLE-STYLE: Big scale, clip overflow, no white gaps
    // ========================================

    const initialScale = 1.4;  // Start 40% larger
    const finalScale = 1.0;

    // Clip the overflow so scaled image doesn't spill outside
    gsap.set(imageWrapper, {
      overflow: 'hidden'
    });

    // Start image visible and scaled up (no fade - prevents layout shift)
    gsap.set(productImage, {
      scale: initialScale,
      opacity: 1,
      transformOrigin: 'center top'
    });

    // Hide trust signals initially
    if (trustSignals) {
      gsap.set(trustSignals, {
        opacity: 0,
        y: 20
      });
    }

    // Scroll distance for animation
    const scrollDistance = window.innerHeight * 0.35;

    // Create scroll animation WITHOUT pinning to avoid whitespace issues
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: hero,
        start: 'top top',
        end: `+=${scrollDistance}`,
        scrub: 0.5
      }
    });

    // Scale down the image as user scrolls
    tl.to(productImage, {
      scale: finalScale,
      ease: 'none',
      duration: 1
    }, 0);

    // Fade in trust signals
    if (trustSignals) {
      tl.to(trustSignals, {
        opacity: 1,
        y: 0,
        ease: 'power2.out',
        duration: 0.4
      }, 0.6);
    }

    console.log('[HeroScroll] Scale animation: ', initialScale, '->', finalScale);
  }
}

// Auto-initialize
function initHeroScroll() {
  const hero = document.querySelector('.pureone-hero');
  if (!hero) return;

  requestAnimationFrame(() => {
    new PureOneHeroScroll();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeroScroll);
} else {
  initHeroScroll();
}

export default PureOneHeroScroll;
